import { Point, Rectangle } from "./geometry";
import { ResourceStatus } from "./enums";
import { Sprite } from "./sprite";
import { Loader } from "./loader";
import { ZoomImageDefinition } from "./zoom-image-definition";
import { MosaicDefinition } from "./mosaic-definition";
import { ZoomImageTile } from "./zoom-image-tile";
import { SpriteManager } from "./sprite-manager";
import { ImageManager } from "./image-manager";
import { ScreenRectangle } from "./screen-rectangle";

/**
 * Class for zoomable images with original and tiled version
 * 
 * TODO: timeout for when the image is not accesible and onload therefore never fires
 */
export class ZoomImage
{
    public status:ResourceStatus = ResourceStatus.Empty;

    public rect:ScreenRectangle = new ScreenRectangle();

    /**
     * Width of content (in pixels)
     */
    public width:number;

    /**
     * Height of content (in pixels)
     */
    public height:number;

    public original:Sprite;
    public mosaic:Sprite;

    public tiles:ZoomImageTile[][] = null;

    public name:string;
    public url:string;

    private _zoom:number = 1.0;
    public get zoom() {return this._zoom;}

    public definition:ZoomImageDefinition;
    public mosaicDefinition:MosaicDefinition;

    public fadeStart:number;
    public fadeEnd:number;

    public viewport:ScreenRectangle;

    public imageManager:ImageManager;
    public spriteManager:SpriteManager;

    /**
     * The number of visible mosaic tiles must decrease to this number and then the loading will start.
     */
    public loadTilesWhen:number = 64;

    /**
     * Flag marking that the process of loading tiles has started
     */
    private tilesLoading:boolean = false;

    /**
     * Reference to global Sprite Manager
     */
    public sprites:SpriteManager;

    private definitionsToLoad:number = 2;

    constructor(url:string)
    {
        this.url = url;
        this.name = url.substr(url.lastIndexOf('/')+1);    

        this.original = new Sprite(this.url + ".jpg");
        this.rect.addChild(this.original.rect);        
        this.original.opacity = 1.0;
        this.original.rect.z = 3;

        this.mosaic = new Sprite(this.url + ".mosaic.jpg");
        this.rect.addChild(this.mosaic.rect);
        this.mosaic.rect.name = "mosaic";
        this.mosaic.opacity = 0.0;
        this.mosaic.rect.z = 2;
    }

    loadDefinition()
    {
        this.status = ResourceStatus.DefinitionLoading;

        // Load image definition
        Loader.Load(this.url + ".json", (data) => {

            this.definition = new ZoomImageDefinition();
            this.definition.fill(data);

            this.width = this.definition.width;
            this.height = this.definition.height;

            if (--this.definitionsToLoad == 0) this.status = ResourceStatus.DefinitionLoaded;

        }, ()=>{this.status = ResourceStatus.Error}, Loader.TYPE_JSON);


        // Load mosaic definition
        Loader.Load(this.url + ".mosaic.json", (data) => {

            this.mosaicDefinition = new MosaicDefinition();
            this.mosaicDefinition.fill(data);
            
            // tiles are deserialized separately because we can't yet deserialize a two-dimensional from JSON
            this.tiles = [];
            for (let row = 0; row < data.tiles.length; row++)
            {
                this.tiles[row] = [];
                for (let col = 0; col < data.tiles[row].length; col++)
                {
                    let t:ZoomImageTile = ZoomImageTile.FromObject(data.tiles[row][col]);
                    t.row = row;
                    t.col = col;
                    this.tiles[row][col] = t;                    
                }
            }

            if (--this.definitionsToLoad == 0) this.status = ResourceStatus.DefinitionLoaded;

        }, ()=>{this.status = ResourceStatus.Error}, Loader.TYPE_JSON);  

    }

    preload()
    {
        this.status = ResourceStatus.Preloading;
      
        // preload original image
        this.original.preload();

        // preload tiled image
        this.mosaic.preload();

        this.tilesLoading = false;
    }

    unload()
    {
        // unload original image
        // unload mosaic image
        // unload all mosaic tile sprites
    }

    /**
     * Set image's local width/height to fit the given viewport width/height
     * @param width 
     * @param height 
     */
    fit(width:number, height:number)
    {
        if (this.height > 0 && height > 0)
        {
            let ar1 = this.width / this.height;
            let ar2 = width / height;
           
            if (ar1 > ar2)
            {
                this.width = width;
                this.height = width / ar1;                
            }
            else
            {
                this.height = height;
                this.width = height * ar1;
            }            
        }        

        this.rect.localWidth = this.width / width;
        this.rect.localHeight = this.height / height;        
        this.rect.recalculate();
    }

    /**
     * Set zoom to the given value with the center of zooming being the given point (in screen coordinates)
     * @param val 
     * @param center 
     */
    setZoom(val:number, center:Point = null)
    {
        // Zooming with a center
        if (center != null && this.rect.parent)
        {
            this.rect.localPosition = this.rect.parent.screenToLocal(center.add(this.rect.screenPosition.sub(center).mul(val / this.zoom)));
        }

        this._zoom = val;

        if (this.zoom < this.fadeStart)
        {
            this.original.opacity = 1.0;     
            this.mosaic.opacity = 0;       
        }
        else if (this.zoom >= this.fadeStart && this.zoom < this.fadeEnd)
        {
            let t = (this.zoom - this.fadeStart) / (this.fadeEnd - this.fadeStart);
            this.mosaic.opacity = t;
        }
        else if (this.zoom > this.fadeEnd)
        {            
            this.mosaic.opacity = 1.0;
        }        

        this.rect.localScale = new Point(this.zoom, this.zoom);       
        this.rect.recalculate();

        // Check the number of visible tiles and load/unload sprites accordingly
        let visible = (this.visibleTiles());
        if (visible.length > 0 && visible.length < this.loadTilesWhen && !this.tilesLoading)
            this.loadTiles(visible);

        // Update opacity of sprites
        this.eachTile( (t) => {if (t.sprite) t.sprite.opacity = this.mosaic.opacity});
    }

    public eachTile( callback : (t:ZoomImageTile) => any)
    {
        for (let row = 0; row < this.tiles.length; row++)
        {
            for (let col = 0; col < this.tiles[row].length; col++)
            {
                callback(this.tiles[row][col]);
            }
        }
    }

    private loadTiles(tilesToLoad:ZoomImageTile[])
    {
        this.tilesLoading = true;
        let dx = this.width * this.zoom / this.mosaicDefinition.tiles_x;
        let dy = this.height * this.zoom / this.mosaicDefinition.tiles_y;
        let u = 1 / this.mosaicDefinition.tiles_x;
        let v = 1 / this.mosaicDefinition.tiles_y;

        for (let i = 0; i < tilesToLoad.length; i++)
        {
            let t:ZoomImageTile = tilesToLoad[i];

            let url = this.imageManager.tileURL(t);

            t.sprite = new Sprite(url);
            t.sprite.opacity = 0;
            t.sprite.fadeIn = 0.2;
            
            t.sprite.rect.localPosition = new Point((t.col + 0.5) * u, (t.row + 0.5) * v);
            t.sprite.rect.localWidth = u;
            t.sprite.rect.localHeight = v;

            t.sprite.width = dx;
            t.sprite.height = dy;

            this.mosaic.rect.addChild(t.sprite.rect);

            this.spriteManager.add(t.sprite);
            t.sprite.preload();
        }
    }

    /**
     * Calculates which tiles are visible. Returns an array of indices of visible tiles
     */
    private visibleTiles():ZoomImageTile[]
    {
        if (this.tiles == null) return [];

        let result:ZoomImageTile[] = [];
        for (let row = 0; row < this.tiles.length; row++)
        {
            for (let col = 0; col < this.tiles[row].length; col++)
            {
                if (this.tileVisible(this.tiles[row][col]))
                    result.push(this.tiles[row][col]);    
            }
        }
        return result;
    }

    public tileVisible(tile:ZoomImageTile):boolean
    {
        let r:Rectangle = this.tileRectangle(this.tiles[tile.row][tile.col]);
        return this.viewport.rect.intersects(r);
    }

    /**
     * Calculates screen rectangle for a tile in the zoom image
     * @param i 
     */
    private tileRectangle(t:ZoomImageTile):Rectangle
    {
        let u:number = t.col / this.mosaicDefinition.tiles_x;
        let v:number = t.row / this.mosaicDefinition.tiles_y;

        let UL:Point = this.rect.screenPosition.add( new Point((u - 0.5) * this.width, (v - 0.5) * this.height).mul(this.zoom));
        let w:number = this.width * this.zoom / this.mosaicDefinition.tiles_x;
        let h:number = this.height * this.zoom / this.mosaicDefinition.tiles_y;        

        return new Rectangle(w, h, UL.x, UL.y);
    }

    /**
     * Returns which tile is coincident with the given screen position
     * @param pos 
     */
    public tileInFocus(pos:Point):ZoomImageTile
    {
        let tile:ZoomImageTile = null;

        this.eachTile( (t:ZoomImageTile) => {
            if (t.contains(pos))
            {
                tile = t;
            }
        });
        return tile;
    }
}